/**
 * 数字与日期格式化（PRD §8.4：数字千分位、日期、时间按语言格式化；时间一律按设备时区显示）。
 * 数字只做字符串截断与分组，金额不经过 JS number（TECH-BRIEF §1）。
 *
 * 日期与时间的固定写法（PRD 5.14 A45「格式」、spec-core §1.9 I6）：
 * 中 2026-11-01、韩 2026. 11. 1.、日 2026/11/01、英 Nov 1, 2026；时间一律 24 小时制 HH:mm。
 */
import { formatDecimal, formatDecimalParts, type DecimalInput, type DecimalParts, type NumberSymbols } from '@nexora/shared';
import { LOCALE_TAGS, type Lang } from './langs.js';

const symbolCache = new Map<Lang, NumberSymbols>();

/** 该语言的千分位与小数点符号（取自 Intl，仅用一个固定样例数字探测符号）。 */
export function numberSymbols(lang: Lang): NumberSymbols {
  const cached = symbolCache.get(lang);
  if (cached) return cached;
  const parts = new Intl.NumberFormat(LOCALE_TAGS[lang], { useGrouping: true }).formatToParts(1234567.5);
  const symbols: NumberSymbols = {
    group: parts.find((p) => p.type === 'group')?.value ?? ',',
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
  };
  symbolCache.set(lang, symbols);
  return symbols;
}

/** 截断到 places 位并按语言加千分位。 */
export function formatNumber(lang: Lang, value: DecimalInput, places: number): string {
  return formatDecimal(value, places, numberSymbols(lang));
}

/** 同 formatNumber，但拆成整数与小数两段（供「整数大、小数小一号」的金额组件使用）。 */
export function formatNumberParts(lang: Lang, value: DecimalInput, places: number): DecimalParts {
  return formatDecimalParts(value, places, numberSymbols(lang));
}

export type DateInput = Date | number | string;

function toDate(date: DateInput): Date {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) throw new RangeError(`Invalid date: ${String(date)}`);
  return value;
}

/** 日期时间格式化；默认按设备时区。 */
export function formatDate(
  lang: Lang,
  date: DateInput,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  return new Intl.DateTimeFormat(LOCALE_TAGS[lang], options).format(toDate(date));
}

/** 某时区下的年月日时分。 */
export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** UTC 偏移写法：+09:00、-05:30、UTC+9、UTC+09:00、UTC（后台按 UTC+9 显示，PRD 5.18 A55）。 */
const OFFSET_TZ = /^(?:UTC|GMT)?\s*([+-])(\d{1,2})(?::?(\d{2}))?$/i;

function offsetMinutes(timeZone: string): number | null {
  const trimmed = timeZone.trim();
  if (/^(?:UTC|GMT|Z)$/i.test(trimmed)) return 0;
  const match = OFFSET_TZ.exec(trimmed);
  if (!match) return null;
  const hours = Number(match[2]);
  const minutes = Number(match[3] ?? '0');
  if (hours > 14 || minutes > 59) return null;
  return (match[1] === '-' ? -1 : 1) * (hours * 60 + minutes);
}

const zonedFormatters = new Map<string, Intl.DateTimeFormat>();

function zonedFormatter(timeZone: string | undefined): Intl.DateTimeFormat {
  const id = timeZone ?? '';
  let formatter = zonedFormatters.get(id);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      ...(timeZone ? { timeZone } : {}),
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
    zonedFormatters.set(id, formatter);
  }
  return formatter;
}

/**
 * 取某时区下的年月日时分。timeZone 可以是 IANA 名称（Asia/Seoul）或 UTC 偏移（+09:00、UTC+9）；
 * 省略时按设备时区（PRD §8.4「时间一律按设备时区显示」）。
 */
export function zonedParts(date: DateInput, timeZone?: string | null): ZonedParts {
  const value = toDate(date);
  const tz = timeZone ?? undefined;
  const offset = tz === undefined ? null : offsetMinutes(tz);
  if (offset !== null) {
    const shifted = new Date(value.getTime() + offset * 60_000);
    return {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth() + 1,
      day: shifted.getUTCDate(),
      hour: shifted.getUTCHours(),
      minute: shifted.getUTCMinutes(),
    };
  }
  const parts = zonedFormatter(tz).formatToParts(value);
  const pick = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return { year: pick('year'), month: pick('month'), day: pick('day'), hour: pick('hour') % 24, minute: pick('minute') };
}

const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** 按语言写日期：中 2026-11-01、韩 2026. 11. 1.、日 2026/11/01、英 Nov 1, 2026。 */
export function formatDay(lang: Lang, date: DateInput, timeZone?: string | null): string {
  const { year, month, day } = zonedParts(date, timeZone);
  switch (lang) {
    case 'zh':
      return `${year}-${pad2(month)}-${pad2(day)}`;
    case 'ko':
      return `${year}. ${month}. ${day}.`;
    case 'ja':
      return `${year}/${pad2(month)}/${pad2(day)}`;
    case 'en':
      return `${EN_MONTHS[month - 1]} ${day}, ${year}`;
  }
}

/** 24 小时制时间 HH:mm（四种语言相同）。 */
export function formatClock(_lang: Lang, date: DateInput, timeZone?: string | null): string {
  const { hour, minute } = zonedParts(date, timeZone);
  return `${pad2(hour)}:${pad2(minute)}`;
}

/** 日期 + 空格 + 时间，例如中 2026-11-01 14:02、英 Nov 1, 2026 14:02。 */
export function formatDayTime(lang: Lang, date: DateInput, timeZone?: string | null): string {
  return `${formatDay(lang, date, timeZone)} ${formatClock(lang, date, timeZone)}`;
}

/** spec-core §1.9 `format(lang)` 的形状：服务端（通知、PDF、CSV）与前端共用。 */
export interface LangFormatter {
  number(value: DecimalInput, places: number): string;
  date(date: DateInput, timeZone?: string | null): string;
  time(date: DateInput, timeZone?: string | null): string;
  dateTime(date: DateInput, timeZone?: string | null): string;
}

export function createFormatter(lang: Lang): LangFormatter {
  return {
    number: (value, places) => formatNumber(lang, value, places),
    date: (date, timeZone) => formatDay(lang, date, timeZone),
    time: (date, timeZone) => formatClock(lang, date, timeZone),
    dateTime: (date, timeZone) => formatDayTime(lang, date, timeZone),
  };
}
